import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    Box,
    Button,
    Chip,
    MenuItem,
    TextField,
    Tooltip,
    Typography,
    useTheme,
} from '@mui/material';
import dayjs, { Dayjs } from 'dayjs';
import {
    CTableWrapper,
    ICustomTableWrapperProps,
    SortedOrder,
} from '../../../../dum';
import { formatFloat } from '../../../../../helpers';
import {
    ProductionPerformanceWeeklyAuditRunsQuery,
    useProductionPerformanceWeeklyAuditRunsQuery,
} from '../../../../../services';
import {
    FlagSeverity,
    FlagUngradable,
    gradeLine,
    HOURLY_DATA_EPOCH,
    MIN_BASELINE_RUNS,
    RatePair,
    THROUGHPUT_BAD_RATIO,
    THROUGHPUT_WARN_RATIO,
    useRatesCache,
} from '../../../../smart/production-performance';
import { usePerfFilters } from '../perf-filters-context';
import { weeklyZFlag, Z_ALERT, Z_WARN, ZLevel } from '../weekly-audit-grade';

// Column indices — must match the `tableItems` order below. Each raw unit sits
// next to its rate (Bultos → Bultos/hr, Kilos → kg/hr). The z column sorts by |z|.
const COL_DATE = 0;
const COL_PRODUCT = 1;
const COL_MACHINE = 2;
const COL_EMPLOYEES = 3;
const COL_BULTOS = 4;
const COL_BULTOS_HR = 5;
const COL_KILOS = 6;
const COL_KG_HR = 7;
const COL_HOURS = 8;
const COL_RATIO = 9;
const COL_Z = 10;
// Index 11 is the non-sortable indicator chip.

type WeeklyRun = ProductionPerformanceWeeklyAuditRunsQuery['getWeeklyAuditRuns'][number];

interface OrderProductionType {
    id: number;
    name: string;
}

interface Branch {
    id: number;
    name: string;
}

interface IWeeklyAuditTabProps {
    types: OrderProductionType[];
    branches: Branch[];
}

// Weekday options for the Día filter. value = dayjs day() index (0 = Sunday …
// 6 = Saturday); the audit's productions are Mon–Sat in practice but Sunday is
// listed for completeness. Labels are the Spanish weekday names the floor uses.
const WEEKDAY_OPTIONS: { value: number; label: string }[] = [
    { value: 1, label: 'Lunes' },
    { value: 2, label: 'Martes' },
    { value: 3, label: 'Miércoles' },
    { value: 4, label: 'Jueves' },
    { value: 5, label: 'Viernes' },
    { value: 6, label: 'Sábado' },
    { value: 0, label: 'Domingo' },
];

interface GradedRow {
    orderProductionId: number;
    dateStr: string | null;
    dateLabel: string;
    productDescription: string;
    machineName: string;
    employeeNames: string;
    groups: number;
    kilos: number;
    hours: number;
    bultosHr: number | null;
    kgHr: number | null;
    // Ratio grade (under-performance, one-directional) from gradeLine.
    ratio: number | null;
    ratioSeverity: FlagSeverity;
    ratioLabel: string;
    // Detail for the Rendimiento tooltip.
    tpValue: number | null; // this run's rate, in the graded unit
    tpBaseline: number | null; // its machine×product (or machine) baseline rate
    tpUnit: 'bultos' | 'kilos';
    tpRuns: number; // runs behind the baseline
    tpSource: 'combo' | 'machine';
    tpUngradable: FlagUngradable | null;
    // z-flag (anomaly, both directions) + detail for the Indicador tooltip.
    z: number | null;
    zLevel: ZLevel;
    zMean: number | null;
    zStd: number | null;
    zN: number;
}

// Monday of the week containing `date`. ISO weeks run Mon–Sun; day() is
// 0 (Sun)–6 (Sat), so the Monday offset is (day + 6) mod 7. Plain local dates —
// start_date is a calendar day, no time-of-day to preserve.
function mondayOf(date: Dayjs): Dayjs {
    const offset = (date.day() + 6) % 7;
    return date.subtract(offset, 'day').startOf('day');
}

// ISO 8601 week number: the week owning `monday` is the one whose Thursday falls
// in the year that numbers it (weeks belong to the year of their Thursday).
function isoWeekNumber(monday: Dayjs): number {
    const thursday = monday.add(3, 'day');
    const jan4 = dayjs(new Date(thursday.year(), 0, 4));
    const jan4Monday = mondayOf(jan4);
    return Math.floor(thursday.diff(jan4Monday, 'day') / 7) + 1;
}

const unitLabel = (u: 'bultos' | 'kilos'): string =>
    u === 'kilos' ? 'kg/hr' : 'bultos/hr';

// Rendimiento tooltip: spell out, in numbers, why the chip is green/amber/red
// against the combo's OWN base — the question the color alone can't answer.
function ratioTooltip(r: GradedRow): React.ReactNode {
    const u = unitLabel(r.tpUnit);
    if (r.tpUngradable === 'pending') {
        return (
            <Typography variant={'caption'}>
                Calculando el rendimiento contra el historial…
            </Typography>
        );
    }
    if (r.tpUngradable === 'thin-history') {
        return (
            <Typography variant={'caption'}>
                Todavía no hay suficiente historial para calificar esta
                combinación máquina × producto: {formatFloat(r.tpRuns)} corridas
                registradas y se requieren {MIN_BASELINE_RUNS}. Se muestra el
                ritmo de la corrida, pero sin comparación.
            </Typography>
        );
    }
    if (r.tpUngradable === 'no-hours') {
        return (
            <Typography variant={'caption'}>
                Esta corrida no registró horas, así que no puede calcularse un
                rendimiento ({u}).
            </Typography>
        );
    }
    const value = r.tpValue !== null ? formatFloat(r.tpValue) : '—';
    const base = r.tpBaseline !== null ? formatFloat(r.tpBaseline) : '—';
    const pct = r.ratio !== null ? formatFloat(r.ratio * 100) : '—';
    const sourceClause =
        r.tpSource === 'machine'
            ? 'el promedio de la máquina (mezcla de sus productos, porque esta combinación tiene poco historial propio)'
            : 'esta combinación máquina × producto';
    const colorClause =
        r.ratioSeverity === 'bad'
            ? `en rojo, porque queda por debajo del ${formatFloat(
                  THROUGHPUT_BAD_RATIO * 100,
              )}% de su base`
            : r.ratioSeverity === 'warn'
            ? `en ámbar, porque está entre el ${formatFloat(
                  THROUGHPUT_BAD_RATIO * 100,
              )}% y el ${formatFloat(THROUGHPUT_WARN_RATIO * 100)}% de su base`
            : `en verde, porque alcanza al menos el ${formatFloat(
                  THROUGHPUT_WARN_RATIO * 100,
              )}% de su base`;
    return (
        <Box>
            <Typography variant={'caption'} sx={{ display: 'block', mb: 0.5 }}>
                Esta corrida rindió {value} {u}. Su base histórica es {base} {u},
                calculada sobre {formatFloat(r.tpRuns)} corridas de{' '}
                {sourceClause} desde {HOURLY_DATA_EPOCH}.
            </Typography>
            <Typography variant={'caption'} sx={{ display: 'block' }}>
                Eso equivale al {pct}% de su base, por lo que se marca{' '}
                {colorClause}.
            </Typography>
        </Box>
    );
}

// Indicador tooltip: why the anomaly flag is atención/alerta and in which
// direction, with the z-score and the μ/σ it was measured against.
function zTooltip(r: GradedRow): React.ReactNode {
    if (r.z === null) {
        return (
            <Typography variant={'caption'}>
                {r.zN > 0 && r.zN < MIN_BASELINE_RUNS
                    ? `No hay suficiente historial para detectar anomalías: ${formatFloat(
                          r.zN,
                      )} corridas y se requieren ${MIN_BASELINE_RUNS}.`
                    : 'Sin base estadística suficiente para evaluar la variación de esta corrida.'}
            </Typography>
        );
    }
    const u = unitLabel(r.tpUnit);
    const value = r.tpValue !== null ? formatFloat(r.tpValue) : '—';
    const mean = r.zMean !== null ? formatFloat(r.zMean) : '—';
    const std = r.zStd !== null ? formatFloat(r.zStd) : '—';
    const high = r.z > 0;
    const dir = high ? 'por encima' : 'por debajo';
    // The second sentence must agree with the Indicador chip, which only shows
    // for zLevel warn/alert. When |z| ≤ 1 (level 'none') no chip is drawn, so the
    // tooltip explains the ABSENCE of a flag rather than claiming one — the bug
    // that made a −0.87 row read "se marca como atención porque |z| supera 1".
    const verdict =
        r.zLevel === 'none' ? (
            <Typography variant={'caption'} sx={{ display: 'block' }}>
                No se marca ninguna anomalía porque |z| no supera {Z_WARN}: la
                corrida se mantiene dentro de la variación habitual de esta
                combinación (aunque su rendimiento pueda ser bajo — eso lo mide
                la columna Rendimiento, no ésta).
            </Typography>
        ) : (
            <Typography variant={'caption'} sx={{ display: 'block' }}>
                Se marca como {r.zLevel === 'alert' ? 'alerta' : 'atención'}{' '}
                porque |z| supera {r.zLevel === 'alert' ? Z_ALERT : Z_WARN}.{' '}
                {high
                    ? 'Un valor tan alto suele ser una corrida excepcional o un posible error de captura; conviene revisarlo.'
                    : 'Un valor tan bajo indica un desempeño por debajo de lo habitual para esta combinación.'}
            </Typography>
        );
    return (
        <Box>
            <Typography variant={'caption'} sx={{ display: 'block', mb: 0.5 }}>
                El ritmo de esta corrida ({value} {u}) está a{' '}
                {formatFloat(Math.abs(r.z))} desviaciones estándar {dir} de su
                promedio histórico (μ = {mean} {u}, σ = {std}, sobre{' '}
                {formatFloat(r.zN)} corridas).
            </Typography>
            {verdict}
        </Box>
    );
}

// Weekly per-corrida audit. Filtered by ISO week (Mon–Sun) + optional order
// production type, NOT by a machine/product combination: every corrida in the
// week is listed and each is graded against its OWN machine×product baseline
// (fetched via useRatesCache). Two signals per row — gradeLine's under-
// performance ratio and a both-directions z-score for the upside the ratio
// misses. Bultos is the primary unit; kg/hr rides along as informational.
export function WeeklyAuditTab({ types, branches }: IWeeklyAuditTabProps) {
    const theme = useTheme();
    const { ensure, get } = useRatesCache();
    // The page-level "Solo producciones de un solo producto" toggle. Multi-product
    // corridas capture hours on their first packed line only, so their per-product
    // bultos/hr is unrecoverable; with the toggle on we both drop those rows and
    // grade the survivors against the single-product baseline (same window key).
    const { singleProductOnly } = usePerfFilters();

    // Anchor day → the week that contains it. Defaults to the current week.
    const [anchor, setAnchor] = useState<string>(() =>
        dayjs().format('YYYY-MM-DD'),
    );
    const [typeId, setTypeId] = useState<number | null>(null);
    // null weekday = "Todos los días"; otherwise a dayjs day() index (0–6).
    const [weekday, setWeekday] = useState<number | null>(null);
    const [branchId, setBranchId] = useState<number | null>(null);
    // Default the type filter to "Corte y empaque" once the catalogue loads —
    // that's the bultos-centric floor everyone talks about. One-shot: after this
    // the user's choice (including "Todos los tipos") stands.
    const typeInitialized = useRef(false);
    useEffect(() => {
        if (typeInitialized.current || types.length === 0) return;
        typeInitialized.current = true;
        const match = types.find((t) => {
            const n = t.name.toLowerCase();
            return n.includes('corte') && n.includes('empaque');
        });
        if (match) setTypeId(match.id);
    }, [types]);
    // Default the branch filter to Caucel (the main plant) once branches load.
    // One-shot, so navigating weeks or picking "Todas las sucursales" sticks.
    const branchInitialized = useRef(false);
    useEffect(() => {
        if (branchInitialized.current || branches.length === 0) return;
        branchInitialized.current = true;
        const caucel = branches.find((b) =>
            b.name.toLowerCase().includes('caucel'),
        );
        if (caucel) setBranchId(caucel.id);
    }, [branches]);

    const monday = useMemo(() => mondayOf(dayjs(anchor)), [anchor]);
    const weekStart = useMemo(() => monday.format('YYYY-MM-DD'), [monday]);
    const weekEnd = useMemo(
        () => monday.add(6, 'day').format('YYYY-MM-DD'),
        [monday],
    );
    const weekNumber = useMemo(() => isoWeekNumber(monday), [monday]);
    const weekRangeLabel = useMemo(() => {
        const end = monday.add(6, 'day');
        return `${monday.format('DD MMM')} – ${end.format('DD MMM YYYY')}`;
    }, [monday]);

    const { data, loading } = useProductionPerformanceWeeklyAuditRunsQuery({
        variables: {
            weekStart,
            weekEnd,
            orderProductionTypeId: typeId,
        },
    });
    const runs = useMemo<WeeklyRun[]>(() => {
        let raw = data?.getWeeklyAuditRuns ?? [];
        // product_count is the whole-production distinct-product count, so this
        // keeps only corridas that made exactly one product — the clean ones.
        if (singleProductOnly) {
            raw = raw.filter((r) => r.product_count === 1);
        }
        // Branch + weekday are client-side facets over the fetched week: the
        // baseline grading is per machine×product regardless of branch or day,
        // so narrowing here doesn't disturb the z/ratio math — it only trims
        // which corridas are listed and graded.
        if (branchId !== null) {
            raw = raw.filter((r) => r.branch_id === branchId);
        }
        if (weekday !== null) {
            raw = raw.filter(
                (r) => r.date != null && dayjs.utc(r.date).day() === weekday,
            );
        }
        return raw;
    }, [data, singleProductOnly, branchId, weekday]);

    // Every distinct combo appearing in the week, plus each machine's fallback
    // pair, so gradeLine has both a combo and a machine baseline to grade
    // against. useRatesCache batches the fetch and caches each pair over the
    // whole-history window (HOURLY_DATA_EPOCH) — the baseline is never the week.
    useEffect(() => {
        if (runs.length === 0) return;
        const pairs: RatePair[] = [];
        const seen = new Set<string>();
        for (const r of runs) {
            const comboKey = `${r.machine_id}:${r.product_id}`;
            if (!seen.has(comboKey)) {
                seen.add(comboKey);
                pairs.push({
                    machineId: r.machine_id,
                    productId: r.product_id,
                });
            }
            const machineKey = `${r.machine_id}:`;
            if (!seen.has(machineKey)) {
                seen.add(machineKey);
                pairs.push({ machineId: r.machine_id, productId: null });
            }
        }
        ensure(pairs, HOURLY_DATA_EPOCH, singleProductOnly);
    }, [runs, ensure, singleProductOnly]);

    const [sortedIndex, setSortedIndex] = useState<number | null>(COL_DATE);
    const [sortedOrder, setSortedOrder] = useState<SortedOrder>('asc');

    // Grade every run. `get` changes identity when the rates cache updates, so a
    // late-arriving aggregate re-runs this and turns a "…" (pending) into a real
    // grade — the same live-fill the Producción list flags use.
    const rows = useMemo<GradedRow[]>(() => {
        return runs.map((r) => {
            const dateStr = r.date
                ? dayjs.utc(r.date).format('YYYY-MM-DD')
                : null;
            const bultosHr = r.hours > 0 ? r.groups / r.hours : null;
            const kgHr = r.hours > 0 ? r.kilos / r.hours : null;
            const rate = get(
                r.machine_id,
                r.product_id,
                HOURLY_DATA_EPOCH,
                singleProductOnly,
            );
            const machineRate = get(
                r.machine_id,
                null,
                HOURLY_DATA_EPOCH,
                singleProductOnly,
            );
            const grade = gradeLine({
                machineId: r.machine_id,
                productId: r.product_id,
                startDate: dateStr ?? HOURLY_DATA_EPOCH,
                kilos: r.kilos,
                groups: r.groups,
                hours: r.hours,
                wasteShare: r.waste_share,
                rate,
                machineRate,
                windowFrom: HOURLY_DATA_EPOCH,
            });
            const zf = weeklyZFlag({
                grade,
                groups: r.groups,
                kilos: r.kilos,
                hours: r.hours,
                comboRate: rate,
                machineRate,
            });

            let ratioLabel: string;
            const tp = grade.throughput;
            if (tp.ungradable === 'pending') ratioLabel = '…';
            else if (tp.ungradable === 'thin-history')
                ratioLabel = `Historial insuf. (${tp.baselineRuns})`;
            else if (tp.ungradable === 'no-hours') ratioLabel = 'Sin horas';
            else if (tp.ratio === null) ratioLabel = '—';
            else
                ratioLabel = `${formatFloat(tp.ratio * 100)}% ${
                    tp.unit === 'bultos' ? 'bultos' : 'kg'
                }`;

            // "07 (lun.) #1" — day, weekday, and the production's turno/shift.
            const dayLabel = dateStr
                ? dayjs.utc(r.date).format('DD (ddd)')
                : '—';
            const dateLabel =
                dateStr && r.shift != null
                    ? `${dayLabel} #${r.shift}`
                    : dayLabel;

            return {
                orderProductionId: r.order_production_id,
                dateStr,
                dateLabel,
                productDescription: r.product_description,
                machineName: r.machine_name,
                employeeNames: r.employee_names,
                groups: r.groups,
                kilos: r.kilos,
                hours: r.hours,
                bultosHr,
                kgHr,
                ratio: tp.ungradable === null ? tp.ratio : null,
                ratioSeverity: tp.severity,
                ratioLabel,
                tpValue: tp.value,
                tpBaseline: tp.baseline,
                tpUnit: tp.unit,
                tpRuns: tp.baselineRuns,
                tpSource: grade.baselineSource,
                tpUngradable: tp.ungradable,
                z: zf.z,
                zLevel: zf.level,
                zMean: zf.mean,
                zStd: zf.std,
                zN: zf.n,
            };
        });
    }, [runs, get, singleProductOnly]);

    const sortedRows = useMemo<GradedRow[]>(() => {
        if (sortedIndex === null || sortedOrder === null) return rows;
        const sorted = [...rows].sort((a, b) => {
            let av: number | string | null;
            let bv: number | string | null;
            switch (sortedIndex) {
                case COL_DATE:
                    av = a.dateStr;
                    bv = b.dateStr;
                    break;
                case COL_PRODUCT:
                    av = a.productDescription;
                    bv = b.productDescription;
                    break;
                case COL_MACHINE:
                    av = a.machineName;
                    bv = b.machineName;
                    break;
                case COL_EMPLOYEES:
                    av = a.employeeNames;
                    bv = b.employeeNames;
                    break;
                case COL_BULTOS:
                    av = a.groups;
                    bv = b.groups;
                    break;
                case COL_BULTOS_HR:
                    av = a.bultosHr;
                    bv = b.bultosHr;
                    break;
                case COL_KILOS:
                    av = a.kilos;
                    bv = b.kilos;
                    break;
                case COL_KG_HR:
                    av = a.kgHr;
                    bv = b.kgHr;
                    break;
                case COL_HOURS:
                    av = a.hours;
                    bv = b.hours;
                    break;
                case COL_RATIO:
                    av = a.ratio;
                    bv = b.ratio;
                    break;
                case COL_Z:
                    av = a.z === null ? null : Math.abs(a.z);
                    bv = b.z === null ? null : Math.abs(b.z);
                    break;
                default:
                    return 0;
            }
            if (av === null && bv === null) return 0;
            if (av === null) return 1; // nulls always last
            if (bv === null) return -1;
            const cmp =
                typeof av === 'string'
                    ? av.localeCompare(bv as string)
                    : (av as number) - (bv as number);
            return sortedOrder === 'asc' ? cmp : -cmp;
        });
        return sorted;
    }, [rows, sortedIndex, sortedOrder]);

    // Rendimiento and the z indicator use different statistics (ratio vs z), so
    // their colors are aligned by MEANING rather than threshold: red/amber always
    // means "worse than expected". A z anomaly is therefore colored by DIRECTION —
    // a LOW outlier is bad (red/amber, agreeing with a poor Rendimiento), a HIGH
    // outlier is unusual-but-good, so it gets info (blue), never red.
    const zColor = (z: number | null, level: ZLevel): string | undefined => {
        if (z === null || level === 'none') return undefined;
        if (z > 0) return theme.palette.info.main;
        return level === 'alert'
            ? theme.palette.error.main
            : theme.palette.warning.main;
    };
    // { color, variant, label } for the z indicator chip, same directional rule.
    const zChip = (
        z: number,
        level: ZLevel,
    ): {
        color: 'error' | 'warning' | 'info';
        variant: 'filled' | 'outlined';
        label: string;
    } => {
        const high = z > 0;
        if (level === 'alert') {
            return high
                ? { color: 'info', variant: 'filled', label: 'Inusual alto' }
                : { color: 'error', variant: 'filled', label: 'Alerta baja' };
        }
        return high
            ? { color: 'info', variant: 'outlined', label: 'Sobre lo normal' }
            : { color: 'warning', variant: 'outlined', label: 'Atención baja' };
    };
    const ratioChipColor = (
        severity: FlagSeverity,
    ): 'error' | 'warning' | 'success' => {
        if (severity === 'bad') return 'error';
        if (severity === 'warn') return 'warning';
        return 'success';
    };

    const tableItems = useMemo<ICustomTableWrapperProps<GradedRow>['items']>(
        () => [
            {
                title: 'Fecha',
                textAlign: 'left',
                cellGrow: 2,
                sortable: true,
                content: (r) => r.dateLabel,
            },
            {
                title: 'Producto',
                textAlign: 'left',
                cellGrow: 3,
                sortable: true,
                content: (r) => r.productDescription || '—',
            },
            {
                title: 'Máquina',
                textAlign: 'left',
                cellGrow: 2,
                sortable: true,
                content: (r) => r.machineName || '—',
            },
            {
                title: 'Empleados',
                textAlign: 'left',
                cellGrow: 3,
                sortable: true,
                content: (r) => r.employeeNames || '—',
            },
            {
                title: 'Bultos',
                textAlign: 'right',
                sortable: true,
                content: (r) => formatFloat(r.groups),
            },
            {
                title: 'Bultos/hr',
                textAlign: 'right',
                sortable: true,
                content: (r) =>
                    r.bultosHr === null ? '—' : formatFloat(r.bultosHr),
            },
            {
                title: 'Kilos',
                textAlign: 'right',
                sortable: true,
                content: (r) => formatFloat(r.kilos),
            },
            {
                title: 'kg/hr',
                textAlign: 'right',
                sortable: true,
                content: (r) => (r.kgHr === null ? '—' : formatFloat(r.kgHr)),
            },
            {
                title: 'Horas',
                textAlign: 'right',
                sortable: true,
                content: (r) => formatFloat(r.hours),
            },
            {
                title: 'Rendimiento',
                textAlign: 'center',
                sortable: true,
                content: (r) => (
                    <Tooltip arrow title={ratioTooltip(r)}>
                        <Box component={'span'} sx={{ cursor: 'help' }}>
                            {r.ratio === null ? (
                                <Typography
                                    component={'span'}
                                    variant={'caption'}
                                    sx={{ color: 'text.disabled' }}
                                >
                                    {r.ratioLabel}
                                </Typography>
                            ) : (
                                <Chip
                                    size={'small'}
                                    color={ratioChipColor(r.ratioSeverity)}
                                    // A machine-fallback grade is a weaker
                                    // baseline (the machine's whole product mix,
                                    // in kg, used because the combo's own history
                                    // is thin). Render it OUTLINED even when it's
                                    // bad/amber — so a red on a thin combo reads
                                    // as less authoritative than a filled red on a
                                    // rich combo baseline — and tag it with a *
                                    // that the caption explains.
                                    variant={
                                        r.tpSource === 'machine' ||
                                        r.ratioSeverity === 'ok'
                                            ? 'outlined'
                                            : 'filled'
                                    }
                                    label={
                                        r.tpSource === 'machine'
                                            ? `${r.ratioLabel} *`
                                            : r.ratioLabel
                                    }
                                />
                            )}
                        </Box>
                    </Tooltip>
                ),
            },
            {
                title: 'Anomalía (z)',
                textAlign: 'right',
                sortable: true,
                content: (r) =>
                    r.z === null ? (
                        '—'
                    ) : (
                        <Typography
                            component={'span'}
                            variant={'body2'}
                            sx={{
                                color: zColor(r.z, r.zLevel),
                                fontWeight:
                                    r.zLevel === 'none' ? undefined : 'bold',
                            }}
                        >
                            {`${r.z >= 0 ? '+' : ''}${formatFloat(r.z)}`}
                        </Typography>
                    ),
            },
            {
                title: 'Indicador',
                textAlign: 'center',
                content: (r) => {
                    const inner =
                        r.z === null || r.zLevel === 'none' ? (
                            <Typography
                                component={'span'}
                                variant={'body2'}
                                sx={{ color: 'text.disabled' }}
                            >
                                —
                            </Typography>
                        ) : (
                            (() => {
                                const c = zChip(r.z, r.zLevel);
                                return (
                                    <Chip
                                        size={'small'}
                                        color={c.color}
                                        variant={c.variant}
                                        label={c.label}
                                    />
                                );
                            })()
                        );
                    return (
                        <Tooltip arrow title={zTooltip(r)}>
                            <Box component={'span'} sx={{ cursor: 'help' }}>
                                {inner}
                            </Box>
                        </Tooltip>
                    );
                },
            },
        ],
        // Theme drives the z colors.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [theme],
    );

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                flex: 1,
                minHeight: 0,
            }}
        >
            <Box sx={{ mb: 2, flexShrink: 0 }}>
                <Typography
                    variant={'caption'}
                    sx={{ color: 'text.secondary', display: 'block' }}
                >
                    Cada corrida se califica contra su propia base
                    máquina×producto sobre TODO el historial (desde{' '}
                    {HOURLY_DATA_EPOCH}), nunca contra la semana. Son dos lentes
                    distintos y pueden discrepar: <b>Rendimiento</b> mide qué tan
                    por debajo de su producción típica quedó (bultos/hr, kg/hr si
                    la línea se pesa). <b>Anomalía (z)</b> mide qué tan inusual es
                    frente a la variación normal de esa combinación: por debajo de
                    lo normal en rojo/ámbar, por encima en azul (inusualmente
                    alto); |z| &gt; 1 atención, |z| &gt; 2 alerta. Se necesitan al
                    menos 10 corridas de historial para calificar. Un{' '}
                    <b>*</b> en Rendimiento indica que se calificó contra el
                    promedio de la máquina (base más débil, en kg) porque la
                    combinación tiene poco historial propio.
                </Typography>
            </Box>

            <Box
                sx={{
                    display: 'flex',
                    gap: 2,
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    mb: 2,
                    flexShrink: 0,
                }}
            >
                <Button
                    size={'small'}
                    variant={'outlined'}
                    onClick={() =>
                        setAnchor(
                            monday.subtract(7, 'day').format('YYYY-MM-DD'),
                        )
                    }
                >
                    ‹ Semana anterior
                </Button>
                <Box sx={{ textAlign: 'center', minWidth: 220 }}>
                    <Typography variant={'subtitle2'}>
                        Semana {weekNumber}
                    </Typography>
                    <Typography
                        variant={'caption'}
                        sx={{ color: 'text.secondary' }}
                    >
                        {weekRangeLabel}
                    </Typography>
                </Box>
                <Button
                    size={'small'}
                    variant={'outlined'}
                    onClick={() =>
                        setAnchor(monday.add(7, 'day').format('YYYY-MM-DD'))
                    }
                >
                    Semana siguiente ›
                </Button>
                <Button
                    size={'small'}
                    onClick={() => setAnchor(dayjs().format('YYYY-MM-DD'))}
                >
                    Semana actual
                </Button>

                <TextField
                    select
                    size={'small'}
                    label={'Tipo de producción'}
                    value={typeId ?? ''}
                    onChange={(e) =>
                        setTypeId(e.target.value ? Number(e.target.value) : null)
                    }
                    sx={{ minWidth: 220 }}
                >
                    <MenuItem value={''}>Todos los tipos</MenuItem>
                    {types.map((t) => (
                        <MenuItem key={t.id} value={t.id}>
                            {t.name}
                        </MenuItem>
                    ))}
                </TextField>

                <TextField
                    select
                    size={'small'}
                    label={'Sucursal'}
                    value={branchId ?? ''}
                    onChange={(e) =>
                        setBranchId(
                            e.target.value ? Number(e.target.value) : null,
                        )
                    }
                    sx={{ minWidth: 160 }}
                >
                    <MenuItem value={''}>Todas las sucursales</MenuItem>
                    {branches.map((b) => (
                        <MenuItem key={b.id} value={b.id}>
                            {b.name}
                        </MenuItem>
                    ))}
                </TextField>

                <TextField
                    select
                    size={'small'}
                    label={'Día'}
                    value={weekday ?? ''}
                    onChange={(e) =>
                        setWeekday(
                            e.target.value === ''
                                ? null
                                : Number(e.target.value),
                        )
                    }
                    sx={{ minWidth: 140 }}
                >
                    <MenuItem value={''}>Todos los días</MenuItem>
                    {WEEKDAY_OPTIONS.map((d) => (
                        <MenuItem key={d.value} value={d.value}>
                            {d.label}
                        </MenuItem>
                    ))}
                </TextField>

                <Box sx={{ ml: 'auto' }}>
                    <Typography
                        variant={'caption'}
                        sx={{ color: 'text.secondary' }}
                    >
                        {sortedRows.length} corridas
                    </Typography>
                </Box>
            </Box>

            {!loading && runs.length === 0 ? (
                <Box sx={{ py: 4, textAlign: 'center', flexShrink: 0 }}>
                    <Typography
                        variant={'body2'}
                        sx={{ color: 'text.secondary' }}
                    >
                        No hay corridas en esta semana
                        {typeId || branchId !== null || weekday !== null
                            ? ' con los filtros seleccionados'
                            : ''}
                        .
                    </Typography>
                </Box>
            ) : (
                <CTableWrapper
                    items={tableItems}
                    data={sortedRows}
                    loading={loading}
                    // Long, unpaginated list — fill the remaining pane height and
                    // be the ONLY vertical scroller, so the sticky header works
                    // and the page shows no second scrollbar.
                    fill
                    sortedIndex={sortedIndex}
                    setSortedIndex={setSortedIndex}
                    sortedOrder={sortedOrder}
                    setSortedOrder={setSortedOrder}
                />
            )}
        </Box>
    );
}
